import {
  auditActorPrincipal,
  auditPrincipalLabel,
  auditSubjectPrincipal,
  useAuditPrincipals,
} from "@/components/auditlogs/audit-principals";
import { AuditPrincipalLink } from "@/components/auditlogs/principals";
import { useQueryState } from "nuqs";
import type { MCPServerEntry } from "@/elements";
import { recommended } from "@/elements/plugins";
import { InsightsConfig, InsightsProvider } from "@/components/insights-dock";
import { INSIGHTS_SUGGESTIONS } from "@/lib/insights-suggestions";
import { ResourceListPage } from "@/components/page-templates";
import { Page } from "@/components/page-layout";
import {
  defineFilters,
  useFilterState,
  type FilterValue,
} from "@/components/filters";
import { SimpleTooltip } from "@/components/ui/Tooltip";
import { Text } from "@/components/ui/Text";
import { Switch } from "@/components/ui/Switch";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { useOrganization, useSession } from "@/contexts/Auth";
import { useSlugs } from "@/contexts/Sdk";
import { useRBAC } from "@/hooks/useRBAC";
import { internalMcpUrl } from "@/hooks/useToolsetUrl";
import { subjectHref } from "@/components/auditlogs/subject-href";
import {
  formatActingSurfaceLabel,
  isNotableActingSurface,
} from "@/lib/audit-surfaces";
import type { AuditLog } from "@gram/client/models/components/auditlog.js";
import { chatSessionsCreate } from "@gram/client/funcs/chatSessionsCreate";
import { useGramContext } from "@gram/client/react-query/_context.js";
import { useAuditLogFacets } from "@gram/client/react-query/auditLogFacets.js";
import { useAuditLogsInfinite } from "@gram/client/react-query/auditLogs.js";
import { useListToolsets } from "@gram/client/react-query/listToolsets.js";
import { Icon } from "@/components/ui/Icon";
import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link } from "react-router";
import { Link as TextLink } from "@/components/ui/Link";
import {
  formatAuditAction,
  formatAuditActionLabel,
  formatSubjectLabel,
  getActorLabel,
  renderVerb,
} from "@/lib/audit-log-format";
import { StructuredDiff } from "@/components/auditlogs/structured-diff";
import {
  ActionIconTile,
  AuditFeedFooter,
  DateGroupHeader,
} from "@/components/auditlogs/feed";
import {
  formatDateHeader,
  formatTimeOnly,
  groupConsecutiveByActor,
  groupLogsByDate,
  type ActorRun,
} from "@/lib/audit-log-feed";
import { cn, getServerURL } from "@/lib/utils";

function StrongName({ children }: { children: ReactNode }) {
  return <strong className="text-foreground font-semibold">{children}</strong>;
}

function getSubjectLabel(log: AuditLog) {
  return log.subjectDisplayName || log.subjectSlug || log.subjectId;
}

// renderVerb already names the role ("sent org admin invite to", "changed invite
// role from x to y for"), so the subject is just the invitee. Repeating it here
// rendered as "... invite to someone@example.com as org admin".
function inviteSubjectText(log: AuditLog) {
  return getSubjectLabel(log);
}

function truncateMiddle(value: string, start = 18, end = 16) {
  if (value.length <= start + end + 1) {
    return value;
  }
  return `${value.slice(0, start)}...${value.slice(-end)}`;
}

const SUBJECT_MONO_CLASS = "font-mono text-xs text-muted-foreground";
// Same mono treatment minus the colour, so the design-system link palette wins.
const SUBJECT_LINK_CLASS = "font-mono text-xs";

const AUDIT_LOG_FILTERS = defineFilters([
  { id: "project", label: "Project", kind: "select" },
  { id: "action", label: "Action", kind: "select" },
  { id: "actor", label: "Actor", kind: "select" },
  { id: "surface", label: "Surface", kind: "select" },
]);

function auditLogCountDescription(count?: number) {
  if (count == null) return undefined;
  return `${count.toLocaleString()} audit log${count === 1 ? "" : "s"}`;
}

// A subject rendered as a link to its detail page. Centralizes the mono styling
// and hover affordance so every linked subject looks identical.
function SubjectLink({
  to,
  title,
  children,
}: {
  to: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <TextLink
      asChild
      size="xs"
      underline={false}
      className={SUBJECT_LINK_CLASS}
    >
      <Link to={to} title={title}>
        {children}
      </Link>
    </TextLink>
  );
}

// The text shown for a linked subject. Mirrors the identifier each subject type
// is keyed on — slug where one exists, otherwise id or display name.
function subjectLinkText(log: AuditLog): string {
  switch (log.subjectType) {
    case "deployment":
      return log.subjectId;
    case "toolset":
    case "mcp_server":
    case "environment":
    case "project":
    case "plugin":
      return log.subjectSlug || log.subjectId;
    default:
      return getSubjectLabel(log);
  }
}

function renderSubject(
  log: AuditLog,
  orgSlug: string,
  identities: ReturnType<typeof useAuditPrincipals>,
  projectSlug?: string,
) {
  const principal = auditSubjectPrincipal(log);
  if (principal) {
    return (
      <span title={principal.urn}>
        <AuditPrincipalLink
          principal={principal}
          projectSlug={projectSlug}
          identities={identities}
          fallback={log.subjectDisplayName || principal.urn}
        />
      </span>
    );
  }
  const monoClass = SUBJECT_MONO_CLASS;

  const href = subjectHref(log, orgSlug);
  if (href) {
    // The humanized label drops most of the identifier, so keep the raw value
    // reachable on hover the way the asset subject below does.
    const raw = subjectLinkText(log);
    return (
      <SubjectLink to={href} title={raw}>
        {formatSubjectLabel(raw, log.subjectType)}
      </SubjectLink>
    );
  }

  if (log.subjectType === "asset") {
    const subjectLabel = getSubjectLabel(log);
    return (
      <SimpleTooltip tooltip={subjectLabel}>
        <span
          className={cn(
            monoClass,
            "inline-block max-w-[34ch] truncate align-bottom",
          )}
        >
          {truncateMiddle(formatSubjectLabel(subjectLabel, log.subjectType))}
        </span>
      </SimpleTooltip>
    );
  }

  if (
    log.action === "organization:webhooks_enabled" ||
    log.action === "organization:webhooks_disabled"
  ) {
    return null;
  }

  if (log.subjectType === "otel_forwarding_config") {
    // The subject ID is a UUID with no human-meaningful slug or display name.
    // The verb text is self-contained (e.g., "enabled OpenTelemetry forwarding
    // to otel.example.com"), so we omit the subject column entirely.
    return null;
  }

  return (
    <span className={monoClass}>
      {formatSubjectLabel(getSubjectLabel(log), log.subjectType)}
    </span>
  );
}

function hasDiff(log: AuditLog): boolean {
  if (log.action.startsWith("deployments:")) {
    return false;
  }
  return log.beforeSnapshot != null || log.afterSnapshot != null;
}

/** Marks the row targeted by j/k keyboard navigation. */
const HIGHLIGHTED_ROW_CLASS = "ring-foreground/40 ring-1 ring-inset";

function AuditLogRow({
  identities,
  log,
  orgSlug,
  timestampMode,
  projectSlug,
  isHighlighted,
  rowRef,
  highlightMatch,
}: {
  log: AuditLog;
  identities: ReturnType<typeof useAuditPrincipals>;
  orgSlug: string;
  timestampMode: "utc" | "local";
  projectSlug?: string;
  isHighlighted?: boolean;
  rowRef?: (el: HTMLDivElement | null) => void;
  highlightMatch?: (text: string) => React.ReactNode;
}) {
  const [diffExpanded, setDiffExpanded] = useState(false);
  const showDiff = hasDiff(log);

  const actorLabel = getActorLabel(log);
  const verbText = renderVerb(log);

  const rowContent = (
    <div className="group flex items-center gap-3 px-4 py-2.5">
      <ActionIconTile action={log.action} />
      <div className="min-w-0 flex-1 text-sm leading-5">
        <span>
          <StrongName>
            <AuditPrincipalLink
              projectSlug={projectSlug}
              principal={auditActorPrincipal(log)}
              identities={identities}
              fallback={actorLabel}
              renderLabel={highlightMatch}
            />
          </StrongName>{" "}
          <span className="text-muted-foreground">
            {highlightMatch ? highlightMatch(verbText) : verbText}
          </span>{" "}
          {renderSubject(log, orgSlug, identities, projectSlug)}
          {isNotableActingSurface(log.actingSurface) && (
            <span
              className="border-border text-muted-foreground ml-2 border px-1.5 py-0.5 align-middle text-xs whitespace-nowrap"
              title={
                log.actingClientId
                  ? `OAuth client: ${log.actingClientId}`
                  : undefined
              }
            >
              via {formatActingSurfaceLabel(log.actingSurface)}
            </span>
          )}
        </span>
        {showDiff && (
          <button
            type="button"
            onClick={() => setDiffExpanded((v) => !v)}
            className="text-link-primary ml-2 text-xs hover:underline"
          >
            {diffExpanded ? "Hide diff ▴" : "Show diff ▾"}
          </button>
        )}
      </div>
      <span className="text-muted-foreground w-[5.5rem] shrink-0 text-right font-mono text-xs tabular-nums">
        {formatTimeOnly(log.createdAt, timestampMode)}
      </span>
    </div>
  );

  if (showDiff && diffExpanded) {
    return (
      <div ref={rowRef} className={cn(isHighlighted && HIGHLIGHTED_ROW_CLASS)}>
        <div
          className={cn("border border-b-0", "border-border bg-card border-b")}
        >
          {rowContent}
        </div>
        <div className="bg-card border border-t-0 px-4 pt-2 pb-3">
          <StructuredDiff log={log} />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rowRef}
      className={cn(
        "transition-colors",
        "border-border bg-card border-b",
        isHighlighted && HIGHLIGHTED_ROW_CLASS,
      )}
    >
      {rowContent}
    </div>
  );
}

type AuditCategory = "configuration" | "security";

const CATEGORY_OPTIONS: {
  value: AuditCategory;
  label: string;
  tooltip: string;
}[] = [
  {
    value: "configuration",
    label: "Configuration",
    tooltip: "Changes people and the Speakeasy team made to settings",
  },
  {
    value: "security",
    label: "Security activity",
    tooltip:
      "Machine access, such as an agent's runtime admitting its workload identity",
  },
];

/**
 * A collapsed run of consecutive security entries by one actor, typically an
 * agent's runtime admitting and withdrawing its workload identity on every
 * start and teardown. Expanding it shows each entry.
 */
function ActorRunRow({
  run,
  expanded,
  onToggle,
  identities,
  projectSlug,
  timestampMode,
  highlightMatch,
  children,
}: {
  run: ActorRun;
  expanded: boolean;
  onToggle: () => void;
  identities: ReturnType<typeof useAuditPrincipals>;
  projectSlug?: string;
  timestampMode: "utc" | "local";
  highlightMatch?: (text: string) => React.ReactNode;
  children: ReactNode;
}) {
  const latest = run.logs[0]!;
  const earliest = run.logs[run.logs.length - 1]!;
  return (
    <div>
      <div className="border-border bg-card group flex items-center gap-3 border-b px-4 py-2.5">
        <ActionIconTile action={latest.action} />
        <div className="min-w-0 flex-1 text-sm leading-5">
          <StrongName>
            <AuditPrincipalLink
              projectSlug={projectSlug}
              principal={auditActorPrincipal(latest)}
              identities={identities}
              fallback={getActorLabel(latest)}
              renderLabel={highlightMatch}
            />
          </StrongName>{" "}
          <span className="text-muted-foreground">
            {run.logs.length} security events
          </span>
          <button
            type="button"
            aria-expanded={expanded}
            onClick={onToggle}
            className="text-link-primary ml-2 text-xs hover:underline"
          >
            {expanded ? "Hide ▴" : "Show ▾"}
          </button>
        </div>
        <span className="text-muted-foreground shrink-0 text-right font-mono text-xs whitespace-nowrap tabular-nums">
          {formatTimeOnly(earliest.createdAt, timestampMode)}–
          {formatTimeOnly(latest.createdAt, timestampMode)}
        </span>
      </div>
      {expanded && <div className="bg-card pl-8">{children}</div>}
    </div>
  );
}

/**
 * Wraps the audit logs page in an InsightsProvider so the AI Insights
 * trigger appears in the breadcrumb bar. Uses the org's first project
 * for Elements session auth (required by the chat API).
 */
function AuditLogsInsightsWrapper({ children }: { children: React.ReactNode }) {
  const organization = useOrganization();
  const { session } = useSession();
  const client = useGramContext();

  const projectSlug = organization.projects[0]?.slug ?? "";
  const { data: toolsetsData, isLoading: isLoadingToolsets } = useListToolsets(
    { gramProject: projectSlug },
    undefined,
    { enabled: Boolean(projectSlug) },
  );

  const getSession = useCallback(async () => {
    const res = await chatSessionsCreate(
      client,
      {
        createRequestBody: {
          embedOrigin: window.location.origin,
        },
      },
      undefined,
      {
        headers: {
          "Gram-Project": projectSlug,
        },
      },
    );
    return res.value?.clientToken ?? "";
  }, [client, projectSlug]);

  const serverURL = getServerURL();

  // Build MCP server entries for all project toolsets
  const mcps = useMemo<MCPServerEntry[] | undefined>(() => {
    if (isLoadingToolsets || !toolsetsData?.toolsets?.length) {
      return undefined;
    }

    return toolsetsData.toolsets.flatMap((toolset) => {
      const url = internalMcpUrl({ slug: projectSlug }, toolset);
      if (!url) return [];
      return [
        {
          url,
          name: toolset.slug,
          environment: toolset.defaultEnvironmentSlug,
        },
      ];
    });
  }, [toolsetsData?.toolsets, projectSlug, isLoadingToolsets]);

  const auditToolsFilter = useCallback(
    ({ toolName }: { toolName: string }) =>
      toolName.includes("audit") ||
      toolName.includes("logs") ||
      toolName.includes("tool_calls"),
    [],
  );

  const mcpConfig = useMemo(
    () => ({
      projectSlug,
      plugins: recommended.except("generative-ui"),
      tools: {
        toolsToInclude: auditToolsFilter,
      },
      api: {
        url: serverURL,
        session: getSession,
        headers: {
          "X-Gram-Source": "dashboard-ai-insights-audit-logs",
        },
      },
      environment: {
        GRAM_SERVER_URL: serverURL,
        GRAM_SESSION_HEADER_GRAM_SESSION: session,
        GRAM_APIKEY_HEADER_GRAM_KEY: "",
        GRAM_PROJECT_SLUG_HEADER_GRAM_PROJECT: projectSlug,
      },
      ...(mcps && mcps.length > 0 && { mcps }),
    }),
    [projectSlug, auditToolsFilter, serverURL, getSession, session, mcps],
  );

  return (
    <InsightsProvider
      mcpConfig={mcpConfig}
      title="Audit Log Insights"
      subtitle="Ask about organization activity, changes, and audit events."
      suggestions={INSIGHTS_SUGGESTIONS["org/audit-logs"]}
    >
      {children}
    </InsightsProvider>
  );
}

export default function OrgAuditLogs(): React.JSX.Element {
  const { hasAnyScope } = useRBAC();
  const organization = useOrganization();
  // Only wrap with InsightsProvider when user has org:read or org:admin
  // and at least one project exists (needed for Elements session auth).
  const showInsights =
    hasAnyScope(["org:read", "org:admin"]) && organization.projects.length > 0;

  const page = <OrgAuditLogsInner />;

  if (!showInsights) return page;

  return <AuditLogsInsightsWrapper>{page}</AuditLogsInsightsWrapper>;
}

function OrgAuditLogsInner() {
  const organization = useOrganization();
  const { orgSlug } = useSlugs();
  const {
    values: rawFilterValues,
    setValue: setFilterValue,
    clearValue: clearFilterValue,
    clearAll: clearAllFilters,
  } = useFilterState(AUDIT_LOG_FILTERS);
  const filterValues = useMemo(
    () => ({
      project:
        rawFilterValues.project === "all" ? null : rawFilterValues.project,
      action: rawFilterValues.action === "all" ? null : rawFilterValues.action,
      actor: rawFilterValues.actor === "all" ? null : rawFilterValues.actor,
      surface:
        rawFilterValues.surface === "all" ? null : rawFilterValues.surface,
    }),
    [rawFilterValues],
  );
  const selectedProjectSlug = filterValues.project ?? "all";
  const selectedAction = filterValues.action ?? "all";
  const selectedActor = filterValues.actor ?? "all";
  const selectedSurface = filterValues.surface ?? "all";
  const hasActiveFilters =
    selectedProjectSlug !== "all" ||
    selectedAction !== "all" ||
    selectedActor !== "all" ||
    selectedSurface !== "all";
  const [timestampMode, setTimestampMode] = useQueryState("time", {
    defaultValue: "utc",
  });
  const [selectedCategory, setSelectedCategory] = useQueryState("category", {
    defaultValue: "configuration",
  });
  const category: AuditCategory =
    selectedCategory === "security" ? "security" : "configuration";

  const tsMode = (timestampMode === "local" ? "local" : "utc") as
    | "utc"
    | "local";

  const projects = useMemo(
    () =>
      [...organization.projects].sort((a, b) => a.slug.localeCompare(b.slug)),
    [organization.projects],
  );

  const { data: facetsData } = useAuditLogFacets!({
    projectSlug:
      selectedProjectSlug === "all" ? undefined : selectedProjectSlug,
    category,
  });

  const actionOptions = useMemo(
    () =>
      (facetsData?.actions ?? []).map((option) => ({
        ...option,
        displayName: formatAuditActionLabel(option.value),
      })),
    [facetsData?.actions],
  );
  const actorOptions = facetsData?.actors ?? [];
  const surfaceOptions = useMemo(
    () =>
      (facetsData?.surfaces ?? []).map((option) => ({
        ...option,
        displayName: formatActingSurfaceLabel(option.value),
      })),
    [facetsData?.surfaces],
  );

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useAuditLogsInfinite({
    projectSlug:
      selectedProjectSlug === "all" ? undefined : selectedProjectSlug,
    action: selectedAction === "all" ? undefined : selectedAction,
    actorId: selectedActor === "all" ? undefined : selectedActor,
    actingSurface: selectedSurface === "all" ? undefined : selectedSurface,
    category,
  });

  const logs = useMemo(
    () => data?.pages.flatMap((page) => page.result.logs) ?? [],
    [data],
  );

  const identities = useAuditPrincipals(logs);
  const dateGroups = useMemo(
    () => groupLogsByDate(logs, tsMode),
    [logs, tsMode],
  );

  // Security entries from one actor arrive in bursts (a runtime writes one on
  // every start and teardown), so each day groups consecutive entries by actor.
  // Configuration changes stay one row each.
  const dateRuns = useMemo(
    () =>
      dateGroups.map((group) => ({
        ...group,
        runs:
          category === "security"
            ? groupConsecutiveByActor(group.logs)
            : group.logs.map((log) => ({
                key: log.id,
                actorId: log.actorId,
                actorType: log.actorType,
                logs: [log],
              })),
      })),
    [dateGroups, category],
  );
  const [expandedRuns, setExpandedRuns] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const toggleRun = useCallback((key: string) => {
    setExpandedRuns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Feed current page state to the AI Insights sidebar as context.
  const insightsContext = useMemo(() => {
    const parts: string[] = [
      "The user is viewing the organization Audit Logs page.",
      `Organization: ${organization.name || orgSlug}`,
    ];
    if (selectedProjectSlug !== "all") {
      parts.push(`Filtered to project: ${selectedProjectSlug}`);
    }
    if (selectedAction !== "all") {
      parts.push(`Filtered to action: ${selectedAction}`);
    }
    if (selectedActor !== "all") {
      parts.push(`Filtered to actor: ${selectedActor}`);
    }
    if (selectedSurface !== "all") {
      parts.push(
        `Filtered to acting surface: ${formatActingSurfaceLabel(selectedSurface)}`,
      );
    }
    parts.push(
      category === "security"
        ? "Showing security activity: machine access such as agents' runtimes admitting their workload identity."
        : "Showing configuration changes.",
    );
    parts.push(`Currently showing ${logs.length} audit log entries.`);
    if (dateGroups.length > 0) {
      const firstDate = dateGroups[0]!.date!;
      const lastDate = dateGroups[dateGroups.length - 1]!.date!;
      parts.push(
        `Date range: ${formatDateHeader(lastDate, tsMode)} to ${formatDateHeader(firstDate, tsMode)}`,
      );
    }
    return parts.join("\n");
  }, [
    organization.name,
    orgSlug,
    selectedProjectSlug,
    selectedAction,
    selectedActor,
    selectedSurface,
    category,
    logs.length,
    dateGroups,
    tsMode,
  ]);

  const logFlatIndices = useMemo(() => {
    const map = new Map<string, number>();
    let idx = 0;
    for (const group of dateGroups) {
      for (const log of group.logs) {
        map.set(log.id, idx++);
      }
    }
    return map;
  }, [dateGroups]);

  // --- Search & keyboard navigation state ---
  const [searchQuery, setSearchQuery] = useState("");
  const [currentLogIndex, setCurrentLogIndex] = useState<number | null>(null);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);

  const logsContainerRef = useRef<HTMLDivElement>(null);
  const logRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Reset navigation state when the log list changes (filters, pagination)
  useEffect(() => {
    setCurrentLogIndex(null);
  }, [logs]);

  const getSearchableText = useCallback(
    (log: AuditLog): string => {
      const actor = auditPrincipalLabel(
        auditActorPrincipal(log),
        identities,
        getActorLabel(log),
      );
      const action = formatAuditAction(log.action);
      const verb = renderVerb(log);
      const subject =
        log.subjectType === "organization_invitation"
          ? inviteSubjectText(log)
          : getSubjectLabel(log);
      return `${actor} ${action} ${verb} ${subject}`;
    },
    [identities],
  );

  const deferredSearchQuery = useDeferredValue(searchQuery);

  const searchMatchIndices = useMemo(() => {
    if (!deferredSearchQuery) return [];
    const query = deferredSearchQuery.toLowerCase();
    const indices: number[] = [];
    logs.forEach((log, index) => {
      if (getSearchableText(log).toLowerCase().includes(query)) {
        indices.push(index);
      }
    });
    return indices;
  }, [deferredSearchQuery, logs, getSearchableText]);

  const effectiveSearchIndex =
    searchMatchIndices.length > 0
      ? Math.min(currentSearchIndex, searchMatchIndices.length - 1)
      : 0;

  // Keyboard navigation and search move through every entry, including ones
  // inside a collapsed run, so landing on one opens its run and scrolls to it.
  const collapsibleRunByIndex = useMemo(() => {
    const map = new Map<number, string>();
    for (const group of dateRuns) {
      for (const run of group.runs) {
        if (run.logs.length < 2) continue;
        for (const log of run.logs) {
          const idx = logFlatIndices.get(log.id);
          if (idx !== undefined) map.set(idx, run.key);
        }
      }
    }
    return map;
  }, [dateRuns, logFlatIndices]);
  useEffect(() => {
    if (currentLogIndex === null) return;
    const key = collapsibleRunByIndex.get(currentLogIndex);
    if (!key || expandedRuns.has(key)) return;
    setExpandedRuns((prev) => new Set(prev).add(key));
    requestAnimationFrame(() => {
      logRefs.current
        .get(currentLogIndex)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [currentLogIndex, collapsibleRunByIndex, expandedRuns]);

  const scrollToLog = useCallback((index: number) => {
    const element = logRefs.current.get(index);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setCurrentLogIndex(index);
  }, []);

  const navigateToResult = useCallback(
    (direction: "next" | "prev") => {
      if (searchMatchIndices.length === 0) return;

      let newIndex: number;
      if (direction === "next") {
        newIndex = (effectiveSearchIndex + 1) % searchMatchIndices.length;
      } else {
        newIndex =
          effectiveSearchIndex === 0
            ? searchMatchIndices.length - 1
            : effectiveSearchIndex - 1;
      }

      setCurrentSearchIndex(newIndex);
      const targetIndex = searchMatchIndices[newIndex];
      if (targetIndex !== undefined) {
        scrollToLog(targetIndex);
      }
    },
    [effectiveSearchIndex, searchMatchIndices, scrollToLog],
  );

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      setCurrentSearchIndex(0);

      if (query) {
        const q = query.toLowerCase();
        const firstMatch = logs.findIndex((log) =>
          getSearchableText(log).toLowerCase().includes(q),
        );
        if (firstMatch !== -1) {
          scrollToLog(firstMatch);
        }
      } else {
        setCurrentLogIndex(null);
      }
    },
    [logs, getSearchableText, scrollToLog],
  );

  const searchRegex = useMemo(() => {
    if (!searchQuery) return null;
    const escaped = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`(${escaped})`, "gi");
  }, [searchQuery]);

  const highlightMatch = useCallback(
    (text: string): React.ReactNode => {
      if (!searchRegex) return text;

      const parts = text.split(searchRegex);
      return (
        <>
          {parts.map((part, i) =>
            part.toLowerCase() === searchQuery.toLowerCase() ? (
              <mark
                key={i}
                className="bg-yellow-200 text-inherit dark:bg-yellow-800"
              >
                {part}
              </mark>
            ) : (
              part
            ),
          )}
        </>
      );
    },
    [searchQuery, searchRegex],
  );

  // Keyboard handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const logsContainer = logsContainerRef.current;
      // Walk through shadow roots to find the real focused element
      let activeElement = document.activeElement;
      while (activeElement?.shadowRoot?.activeElement) {
        activeElement = activeElement.shadowRoot.activeElement;
      }
      const isWithinLogsSection = logsContainer?.contains(
        activeElement as Node,
      );

      if (e.key === "Escape") {
        if (isWithinLogsSection) {
          e.preventDefault();
          setSearchQuery("");
          setCurrentLogIndex(null);
          setCurrentSearchIndex(0);
          const el = document.activeElement as HTMLElement;
          if (el && el.tagName === "INPUT") {
            el.blur();
          }
        }
        return;
      }

      const isInInput =
        activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "TEXTAREA" ||
        activeElement?.tagName === "SELECT" ||
        activeElement?.closest("[contenteditable]") !== null;
      if (!isInInput) {
        switch (e.key) {
          case "n":
            if (searchMatchIndices.length > 0) {
              e.preventDefault();
              navigateToResult("next");
            }
            break;
          case "N":
            if (e.shiftKey && searchMatchIndices.length > 0) {
              e.preventDefault();
              navigateToResult("prev");
            }
            break;
          case "j":
            e.preventDefault();
            if (currentLogIndex !== null && currentLogIndex < logs.length - 1) {
              scrollToLog(currentLogIndex + 1);
            } else if (currentLogIndex === null && logs.length > 0) {
              scrollToLog(0);
            }
            break;
          case "k":
            e.preventDefault();
            if (currentLogIndex !== null && currentLogIndex > 0) {
              scrollToLog(currentLogIndex - 1);
            }
            break;
          case "g":
            if (!e.shiftKey && !e.ctrlKey && logs.length > 0) {
              e.preventDefault();
              scrollToLog(0);
            }
            break;
          case "G":
            if (e.shiftKey && logs.length > 0) {
              e.preventDefault();
              scrollToLog(logs.length - 1);
            }
            break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    currentLogIndex,
    logs.length,
    navigateToResult,
    scrollToLog,
    searchMatchIndices.length,
  ]);

  return (
    <ResourceListPage
      scope="org:read"
      title="Audit Logs"
      description="Review changes to your organization's configuration, and security activity such as agents' runtimes admitting their workload identity. Filter by project, action, or actor."
    >
      <div className="flex w-full flex-col gap-4">
        <InsightsConfig contextInfo={insightsContext} />

        <div
          onKeyUp={(event) => {
            if (
              event.key === "Escape" &&
              event.target instanceof HTMLInputElement
            ) {
              event.target.blur();
            }
          }}
        >
          <Page.Toolbar>
            <Page.Toolbar.Leading>
              <SegmentedControl
                ariaLabel="Audit log category"
                value={category}
                onChange={(value) => {
                  // Actions, actors and surfaces differ between categories, so
                  // a filter chosen in one would leave the other empty.
                  void setSelectedCategory(value);
                  clearFilterValue("action");
                  clearFilterValue("actor");
                  clearFilterValue("surface");
                }}
                options={CATEGORY_OPTIONS}
              />
            </Page.Toolbar.Leading>
            <Page.Toolbar.Search
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search audit logs"
            />
            <Page.Toolbar.Filters
              schema={AUDIT_LOG_FILTERS}
              values={filterValues}
              optionsById={{
                project: projects.map((project) => ({
                  value: project.slug,
                  label: project.slug,
                })),
                action: actionOptions.map((option) => ({
                  value: option.value,
                  label: option.displayName,
                  description: auditLogCountDescription(option.count),
                })),
                actor: actorOptions.map((option) => ({
                  value: option.value,
                  label: option.displayName,
                  description: auditLogCountDescription(option.count),
                })),
                surface: surfaceOptions.map((option) => ({
                  value: option.value,
                  label: option.displayName,
                  description: auditLogCountDescription(option.count),
                })),
              }}
              onChange={
                setFilterValue as (id: string, value: FilterValue) => void
              }
              onClear={clearFilterValue as (id: string) => void}
              onClearAll={clearAllFilters}
            />
            <Page.Toolbar.Actions>
              <div className="flex items-center gap-3">
                {searchQuery ? (
                  <div className="text-muted-foreground flex items-center gap-1 text-[11px]">
                    <span aria-live="polite" className="min-w-8 text-center">
                      {searchMatchIndices.length > 0
                        ? `${effectiveSearchIndex + 1}/${searchMatchIndices.length}`
                        : "0/0"}
                    </span>
                    <button
                      onClick={() => navigateToResult("prev")}
                      disabled={searchMatchIndices.length === 0}
                      className="hover:bg-muted p-1 opacity-60 transition-opacity hover:opacity-100 disabled:pointer-events-none disabled:opacity-30"
                      title="Previous (Shift+N)"
                      aria-label="Previous search result"
                    >
                      <Icon name="chevron-up" className="size-3" />
                    </button>
                    <button
                      onClick={() => navigateToResult("next")}
                      disabled={searchMatchIndices.length === 0}
                      className="hover:bg-muted p-1 opacity-60 transition-opacity hover:opacity-100 disabled:pointer-events-none disabled:opacity-30"
                      title="Next (N)"
                      aria-label="Next search result"
                    >
                      <Icon name="chevron-down" className="size-3" />
                    </button>
                  </div>
                ) : null}
                <Text small muted>
                  Timestamp
                </Text>
                <div className="bg-background flex h-8 items-center gap-2 border px-3">
                  <Text
                    small
                    className={
                      tsMode === "utc"
                        ? "text-foreground"
                        : "text-muted-foreground"
                    }
                  >
                    UTC
                  </Text>
                  <Switch
                    checked={tsMode === "local"}
                    onCheckedChange={(checked) => {
                      void setTimestampMode(checked ? "local" : "utc");
                    }}
                    aria-label="Toggle timestamp timezone"
                  />
                  <Text
                    small
                    className={
                      tsMode === "local"
                        ? "text-foreground"
                        : "text-muted-foreground"
                    }
                  >
                    Local
                  </Text>
                </div>
              </div>
            </Page.Toolbar.Actions>
          </Page.Toolbar>
        </div>

        <div className="bg-card overflow-hidden border">
          <div
            ref={logsContainerRef}
            tabIndex={0}
            className="focus:outline-none"
          >
            {isLoading ? (
              <div className="text-muted-foreground flex items-center justify-center gap-2 py-12">
                <Icon name="loader-circle" className="size-4 animate-spin" />
                <span>Loading audit logs...</span>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center gap-2 py-12 text-center">
                <Text className="font-medium">Error loading audit logs</Text>
                <Text muted small>
                  {error.message}
                </Text>
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-12 text-center">
                <Text className="font-medium">
                  {category === "security"
                    ? "No security activity found"
                    : "No audit logs found"}
                </Text>
                <Text muted small>
                  {hasActiveFilters
                    ? "No audit logs match the selected filters."
                    : category === "security"
                      ? "Machine access appears here, such as an agent's runtime admitting its workload identity."
                      : "Activity will appear here as changes are made across your organization."}
                </Text>
              </div>
            ) : (
              <div>
                {dateRuns.map((group) => (
                  <React.Fragment key={group.key}>
                    <DateGroupHeader date={group.date} mode={tsMode} />
                    {group.runs.map((run) => {
                      const rows = run.logs.map((log) => {
                        const idx = logFlatIndices.get(log.id) ?? 0;
                        return (
                          <AuditLogRow
                            key={log.id}
                            log={log}
                            identities={identities}
                            projectSlug={
                              log.projectSlug ||
                              (selectedProjectSlug === "all"
                                ? undefined
                                : selectedProjectSlug)
                            }
                            orgSlug={orgSlug ?? ""}
                            timestampMode={tsMode}
                            isHighlighted={idx === currentLogIndex}
                            rowRef={(el) => {
                              if (el) logRefs.current.set(idx, el);
                              else logRefs.current.delete(idx);
                            }}
                            highlightMatch={
                              searchQuery ? highlightMatch : undefined
                            }
                          />
                        );
                      });
                      // A lone entry is returned as its own keyed row, not an
                      // array, so a new entry at the top doesn't remount it.
                      if (run.logs.length < 2) return rows[0];
                      return (
                        <ActorRunRow
                          key={run.key}
                          run={run}
                          expanded={expandedRuns.has(run.key)}
                          onToggle={() => {
                            // Collapsing the run that holds the selected entry
                            // clears the selection; otherwise the selection
                            // would open the run again straight away.
                            if (
                              expandedRuns.has(run.key) &&
                              currentLogIndex !== null &&
                              collapsibleRunByIndex.get(currentLogIndex) ===
                                run.key
                            ) {
                              setCurrentLogIndex(null);
                            }
                            toggleRun(run.key);
                          }}
                          identities={identities}
                          projectSlug={
                            run.logs[0]!.projectSlug ||
                            (selectedProjectSlug === "all"
                              ? undefined
                              : selectedProjectSlug)
                          }
                          timestampMode={tsMode}
                          highlightMatch={
                            searchQuery ? highlightMatch : undefined
                          }
                        >
                          {rows}
                        </ActorRunRow>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          <AuditFeedFooter
            count={logs.length}
            hasNextPage={hasNextPage ?? false}
            isFetching={isFetching}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => {
              void fetchNextPage();
            }}
          />
        </div>
      </div>
    </ResourceListPage>
  );
}
